import React from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import * as ReactDOMClient from 'react-dom/client';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Capsule from './components/capsule/Capsule';
import { applyHtmlLang } from './helper';
import './i18n'; // Initialize i18n

// Declare global type for widget API
declare global {
  interface Window {
    SeminargoWidget: {
      init: () => void;
    };
  }
}

interface Styles {
  label: string;
  button: string;
  input: string;
  custom: string;
  cssUrls: string[];
}

interface IFormInputs {
  name: string;
  lastname: string;
}

/**
 * Adds an external stylesheet to the host document.
 * @param url - Absolute URL of the stylesheet file.
 * @returns void
 */
function addStylesheetURL(url: string) {
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.getElementsByTagName('head')[0].appendChild(link);
}

// const styles = JSON.parse(host?.getAttribute('data-styles') || '{}');
let styles: Styles | null = null;

// Store React root globally so we can unmount if needed
let reactRoot: ReactDOMClient.Root | null = null;

/**
 * Initializes or re-initializes the widget mount process.
 * @returns Promise that resolves when rendering setup finished.
 */
const initializeWidget = async () => {
  // Get our shadow HOST
  const host = document.querySelector('#seminargo-widget');

  if (!host) {
    console.warn('Seminargo widget: No element with id "seminargo-widget" found');
    return;
  }

  // Hosts can opt into immediate host synchronization after state changes.
  const autoUpdateAttr = host?.getAttribute('data-auto-update');
  const autoUpdate = autoUpdateAttr === 'true' || autoUpdateAttr === '1';

  // Cleanup old React root if it exists
  // Wrap in try-catch in case the DOM element no longer exists
  if (reactRoot) {
    try {
      reactRoot.unmount();
    } catch (e) {
      console.warn('Could not unmount old widget root:', e);
    }
    reactRoot = null;
  }

  // Use a single shared init path for first load and re-mounts.
  await main(host, autoUpdate);
};

/**
 * Resolves widget config and renders Capsule into the shadow root.
 * @param host - Host DOM node with `#seminargo-widget`.
 * @param autoUpdate - Enables automatic update behavior in Capsule.
 * @returns Promise that resolves after rendering is triggered.
 */
const main = async (host: Element, autoUpdate: boolean) => {
  /*const res = await fetch(
    process.env.BACKEND_URL
      ? "${process.env.BACKEND_URL}/style-mock"
      : "http://127.0.0.1:4030/style-mock"
  );
  styles = await res.json();*/

  /**
   * Converts host and query string flags into booleans.
   * @param src - Raw attribute or query parameter value.
   * @returns Whether the value should be interpreted as enabled.
   */
  const _toBool = (src: string | null | undefined) => {
    if (src == null || src == undefined) return false;
    if (src === 'N' || src === '0' || src === 'false') return false;
    return true;
  };

  const urlParams = new URLSearchParams(window.location.search);

  const hotelRef = urlParams.get('seminargoHotelRef') || host?.getAttribute('data-hotel-ref') || undefined;
  const rawPlatformUrl = urlParams.get('seminargoPlatformUrl') || host?.getAttribute('data-platform-url') || '';
  // Normalize to avoid double-slash URL concatenation later (e.g. "https://host.tld/" + "/pricelist/graphql")
  const platformUrl = rawPlatformUrl.trim().replace(/\/+$/, '');
  const hotelView = urlParams.get('seminargoHotelView') || host?.getAttribute('data-hotel-view') || null;
  const language = urlParams.get('seminargoLanguage') || host?.getAttribute('data-language') || null;
  const presetPlaceName = host?.getAttribute('data-place-name') || null;
  const htmlLang = applyHtmlLang(
    language === 'en' || language === 'de' ? language : window.location.pathname.startsWith('/en') ? 'en' : 'de',
  );
  const navDebug = urlParams.get('seminargoNavDebug') === 'true' || urlParams.get('seminargoNavDebug') === '1';

  const s = urlParams && urlParams.get('s')

  const styles = {
    label: '',
    button: '',
    input: '',
    custom: '',
    cssUrls: [],
  };

  const Label = styled.label`
    display: block;
    ${styles && styles.label}
  `;

  const Button = styled.input`
    ${styles && styles.button}
  `;

  const Input = styled.input`
    ${styles && styles.input}
  `;

  const Container = styled.div``;

  const ErrorText = styled.div`
    color: red;
  `;

  const Row = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  const Column = styled.div`
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;
    width: 0;
    text-align: left;
  `;

  const InputContainer = styled.div`
    padding: 10px;
    justify-content: start;
  `;

  let color: string = "#000"

  const time = new Date().getTime();
  let fontsCss = '';
  try {
    const res = await fetch(`${process.env.REACT_APP_WIDGET_URL}/fonts.css?v=${time}`);
    fontsCss = await res.text();
  } catch (err) {
    console.error(`Error fetching fonts.css: ${err}`);
  }

  /**
   * Renders the widget shell and forwards runtime configuration to Capsule.
   * @param shadowRoot - Shadow root used by nested portal-based UI.
   * @returns Widget root tree.
   */
  const App = ({ shadowRoot }: any) => {
    if (process.env.REACT_APP_VERSION) {
      console.log(`SEM-Widget version: ${process.env.REACT_APP_VERSION}`);
    }
    if (process.env.REACT_APP_BUILD_TIMESTAMP) {
      console.log(`SEM-Widget build: ${process.env.REACT_APP_BUILD_TIMESTAMP}`);
    }

    return (
      <div id="seminargoWidgetRoot" style={{ all: 'initial' }} lang={htmlLang}>
        <style>{fontsCss}</style>
        <center>
          <Capsule
            shadowRoot={shadowRoot}
            formState={s}
            platformUrl={platformUrl}
            label="Seminar buchen"
            hotelView={hotelView}
            autoUpdate={autoUpdate}
            language={language}
            navDebug={navDebug}
            presetPlaceName={presetPlaceName}
          />
        </center>
      </div>
    );
  };

  if (host) {
    // Reusing the same shadow root keeps host integrations stable on re-init.
    let shadow = host.shadowRoot;
    if (!shadow) {
      shadow = host.attachShadow({ mode: 'open' });
    } else {
      // Clear previous content to avoid duplicate UI trees after repeated init calls.
      while (shadow.firstChild) {
        shadow.removeChild(shadow.firstChild);
      }
    }

    // create a slot where we will attach the StyleSheetManager
    const styleSlot = document.createElement('section');
    styleSlot.innerHTML = '<style>:host { all: initial; } </style>';
    // append the styleSlot inside the shadow
    shadow.appendChild(styleSlot);

    // create a slot where we will attach the StyleSheetManager
    const styleSlotAdvanced = document.createElement('style');
    styleSlotAdvanced.innerHTML = (styles && styles.custom) || '';

    const pacStyles = document.createElement('style');
    pacStyles.innerHTML = `.pac-container { z-index: 999999 !important; }`;
    document.head.appendChild(pacStyles);

    // append the styleSlot inside the shadow
    shadow.appendChild(styleSlotAdvanced);

    const time = new Date().getTime();
    const fontsCss = document.createElement('link');
    fontsCss.setAttribute('href', `${process.env.REACT_APP_WIDGET_URL}/fonts.css?v=${time}`);
    fontsCss.setAttribute('rel', 'stylesheet');
    fontsCss.setAttribute('type', 'text/css');
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(fontsCss);

    // create the element where we would render our app
    const renderIn = document.createElement('div');
    // append the renderIn element inside the styleSlot
    styleSlot.appendChild(renderIn); 

    if (styles && styles.cssUrls) {
      styles.cssUrls.forEach(url => {
        addStylesheetURL(url);
      });
    }

    reactRoot = ReactDOMClient.createRoot(renderIn);

    // render the app
    reactRoot.render(
      <StyleSheetManager target={styleSlot}>
        <App shadowRoot={shadow} />
      </StyleSheetManager>,
    );
  }
};

// Expose global API for re-initialization
if (typeof window !== 'undefined') {
  window.SeminargoWidget = {
    init: initializeWidget
  };
}

// Initial load
initializeWidget();
